/**
 * Controlled transformation DSL for event mappings.
 *
 * Only a fixed set of operators is supported. No arithmetic, conditionals,
 * string interpolation, or general-purpose code. The DSL is deliberately not
 * turing-complete: every expression is one of a small, code-defined set of
 * operators, and arbitrary JavaScript is rejected at validation time.
 */
import { money, moneyToDecimalString } from '@ibe/utils';
export const TRANSFORM_REGISTRY = [
    'minorToMajor',
    'dateIsoToUnix',
    'firstItem',
    'itemFlatten',
];
const MINOR_PER_MAJOR = 100;
const MILLISECONDS_PER_SECOND = 1000;
const MAX_OUTPUT_KEYS = 100;
const MAX_KEY_LENGTH = 200;
function minorToMajorValue(value) {
    if (value !== null && typeof value === 'object') {
        const candidate = value;
        if (typeof candidate.amountMinor === 'bigint' && typeof candidate.currency === 'string') {
            const decimal = moneyToDecimalString(money(candidate.amountMinor, candidate.currency));
            const parsed = Number.parseFloat(decimal);
            return Number.isNaN(parsed) ? decimal : parsed;
        }
    }
    if (typeof value === 'bigint') {
        return Number(value) / MINOR_PER_MAJOR;
    }
    if (typeof value === 'number') {
        return value / MINOR_PER_MAJOR;
    }
    if (typeof value === 'string') {
        const big = BigInt(value);
        return Number(big) / MINOR_PER_MAJOR;
    }
    return value;
}
const transformFunctions = {
    minorToMajor: minorToMajorValue,
    dateIsoToUnix: (value) => {
        if (typeof value !== 'string')
            return value;
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? value : Math.floor(parsed / MILLISECONDS_PER_SECOND);
    },
    firstItem: (value) => {
        if (!Array.isArray(value) || value.length === 0)
            return undefined;
        return value[0];
    },
    itemFlatten: (value) => {
        if (!Array.isArray(value))
            return value;
        return value.map((item) => {
            if (typeof item === 'object' && item !== null)
                return item;
            return item;
        });
    },
};
function evaluateOperator(event, operator) {
    switch (operator.kind) {
        case 'field':
            return Object.hasOwn(event, operator.name) ? event[operator.name] : undefined;
        case 'literal':
            return operator.value;
        case 'default': {
            const inner = evaluateOperator(event, operator.inner);
            return inner ?? operator.default;
        }
        case 'transform': {
            const inner = evaluateOperator(event, operator.inner);
            const fn = transformFunctions[operator.transform];
            return fn(inner);
        }
        case 'rename':
        case 'omit':
            return undefined;
    }
}
function normalize(spec) {
    if (spec.omit === true) {
        return { kind: 'omit' };
    }
    if (spec.rename !== undefined && typeof spec.rename === 'string' && spec.rename.length > 0) {
        return {
            kind: 'rename',
            target: spec.rename,
            inner: coreOperator(spec),
        };
    }
    return coreOperator(spec);
}
function coreOperator(spec) {
    let inner;
    if (spec.field !== undefined) {
        inner = { kind: 'field', name: spec.field };
    }
    else if (spec.literal !== undefined) {
        inner = { kind: 'literal', value: spec.literal };
    }
    else {
        inner = { kind: 'literal', value: undefined };
    }
    if (spec.default !== undefined) {
        inner = { kind: 'default', default: spec.default, inner };
    }
    if (spec.transform !== undefined && typeof spec.transform === 'string') {
        inner = { kind: 'transform', transform: spec.transform, inner };
    }
    return inner;
}
export function applyMapping(event, mapping) {
    const result = {};
    for (const [outputKey, spec] of Object.entries(mapping)) {
        const operator = normalize(spec);
        if (operator.kind === 'omit')
            continue;
        if (operator.kind === 'rename') {
            const value = evaluateOperator(event, operator.inner);
            result[operator.target] = value;
            continue;
        }
        const value = evaluateOperator(event, operator);
        result[outputKey] = value;
    }
    return result;
}
const FORBIDDEN_SUBSTRINGS = [
    'eval(',
    'Function(',
    'new Function',
    'javascript:',
    'setTimeout(',
    'setInterval(',
];
const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype'];
/**
 * Server-side validation of a `parameter_mapping` JSON object.
 *
 * Returns `null` when valid. Returns a human-readable reason otherwise.
 * Rejects arbitrary expressions, unknown transforms, and executable payloads.
 */
export function validateParameterMapping(mapping) {
    if (mapping === null || typeof mapping !== 'object' || Array.isArray(mapping)) {
        return 'parameterMapping must be an object';
    }
    const record = mapping;
    const keys = Object.keys(record);
    if (keys.length > MAX_OUTPUT_KEYS) {
        return `parameterMapping exceeds the maximum of ${String(MAX_OUTPUT_KEYS)} output keys`;
    }
    for (const key of keys) {
        if (FORBIDDEN_KEYS.includes(key) || key.length > MAX_KEY_LENGTH) {
            return `invalid output key: ${key}`;
        }
        const spec = record[key];
        if (spec === null || typeof spec !== 'object' || Array.isArray(spec)) {
            return `mapping for "${key}" must be an object`;
        }
        const s = spec;
        const reason = validateMappingSpec(s);
        if (reason !== null)
            return `mapping for "${key}": ${reason}`;
    }
    return null;
}
function validateMappingSpec(spec) {
    const allowedKeys = new Set([
        'field',
        'literal',
        'default',
        'transform',
        'omit',
        'rename',
        'condition',
    ]);
    for (const key of Object.keys(spec)) {
        if (!allowedKeys.has(key)) {
            return `unknown operator: ${key}`;
        }
    }
    if (spec.omit === true) {
        return null;
    }
    const hasField = spec.field !== undefined;
    const hasLiteral = spec.literal !== undefined;
    const hasRename = spec.rename !== undefined;
    if (hasField && (typeof spec.field !== 'string' || spec.field.length === 0)) {
        return 'field must be a non-empty string';
    }
    if (hasRename && (typeof spec.rename !== 'string' || spec.rename.length === 0)) {
        return 'rename target must be a non-empty string';
    }
    const sources = [hasField, hasLiteral].filter(Boolean).length;
    if (sources > 1) {
        return 'only one of field or literal may be specified';
    }
    if (sources === 0 && !hasRename) {
        return 'at least one of field, literal, or rename must be specified';
    }
    if (spec.transform !== undefined) {
        if (typeof spec.transform !== 'string' ||
            !TRANSFORM_REGISTRY.includes(spec.transform)) {
            return `unknown transform: ${typeof spec.transform}`;
        }
    }
    if (containsExecutablePayload(spec)) {
        return 'mapping contains forbidden executable content';
    }
    return null;
}
function containsExecutablePayload(value) {
    if (value === null || value === undefined)
        return false;
    if (typeof value === 'function')
        return true;
    if (typeof value === 'string') {
        const lower = value.toLowerCase();
        return FORBIDDEN_SUBSTRINGS.some((s) => lower.includes(s.toLowerCase()));
    }
    if (typeof value === 'object') {
        if (Array.isArray(value)) {
            return value.some(containsExecutablePayload);
        }
        for (const [key, child] of Object.entries(value)) {
            if (FORBIDDEN_KEYS.includes(key))
                return true;
            if (containsExecutablePayload(child))
                return true;
        }
    }
    return false;
}
//# sourceMappingURL=transformation-dsl.js.map